// cleanSlate top-bar behaviour — the menuPosition='header' layout's horizontal main
// menu (views/partials/header.tpl) and its account dropdown. The sidebar layout's
// counterpart is scripts/sidebar.js; both are plain chrome scripts (not behaviour
// adapters) because they drive the theme's own layout markup, not a component.
//
// All CSP-safe: wired with addEventListener, no inline handlers like the design
// mock's onclick="...", and the only style writing is through the CSSOM (none here —
// the panels are plain absolute-positioned CSS, the header is not a card so nothing
// clips them; see the "floating panels must be fixed" note in app/docs/themes).
export default function initTopnav() {
    const header = document.querySelector('.zit-cs-header');

    // sidebar layout (or a chrome-less page) — nothing to wire
    if (!header) {
        return;
    }

    initDropdowns(header);
    initMobilePanel(header);
    initAccountMenu(header);
    initDismiss(header);
}

// Gap (px) kept between an open dropdown's right edge and the viewport edge before
// the panel is flipped to open leftwards instead.
const EDGE_GAP = 8;

// Below this width the nav is not a bar any more: it becomes a panel dropped under the
// header by the hamburger button (kept in sync with the CSS media query).
const SMALL_SCREEN = '(max-width: 1023px)';

// 1) Tab dropdowns — a tab with children renders a trigger + a panel hidden until its
// .zit-cs-topnav-item carries `open` (CSS shows the panel and flips the caret).
// Opening one closes its siblings, so only a single panel is ever open.
function initDropdowns(header) {
    const triggers = header.querySelectorAll('.zit-cs-topnav-trigger');

    triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
            const item = trigger.closest('.zit-cs-topnav-item');

            if (!item) {
                return;
            }

            const wasOpen = item.classList.contains('open');

            closeDropdowns(header);

            if (!wasOpen) {
                item.classList.add('open');
                trigger.setAttribute('aria-expanded', 'true');
                flipIfOffscreen(item);
            }
        });
    });
}

// Close every open tab dropdown (and resync its trigger's aria-expanded).
function closeDropdowns(header) {
    const openItems = header.querySelectorAll('.zit-cs-topnav-item.open');

    openItems.forEach(function (item) {
        item.classList.remove('open');

        const trigger = item.querySelector('.zit-cs-topnav-trigger');

        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
        }
    });
}

// A panel of a tab near the right edge would overflow the viewport; the flip class
// re-anchors it to the item's right edge instead. Measured after opening (the panel
// has no size while hidden) and only on wide screens — below the breakpoint the panel
// is a static, full-width block inside the mobile nav.
function flipIfOffscreen(item) {
    const menu = item.querySelector('.zit-cs-topnav-menu');

    if (!menu) {
        return;
    }

    menu.classList.remove('zit-cs-topnav-menu-left');

    if (window.matchMedia(SMALL_SCREEN).matches) {
        return;
    }

    const rect = menu.getBoundingClientRect();

    if (rect.right > window.innerWidth - EDGE_GAP) {
        menu.classList.add('zit-cs-topnav-menu-left');
    }
}

// 2) Mobile panel — the hamburger shows/hides the whole nav under the bar.
function initMobilePanel(header) {
    const toggle = header.querySelector('.zit-cs-topnav-toggle');
    const nav = header.querySelector('.zit-cs-topnav');

    if (!toggle || !nav) {
        return;
    }

    toggle.addEventListener('click', function () {
        const open = nav.classList.toggle('zit-cs-topnav-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

        // A closing panel leaves nothing expanded behind it.
        if (!open) {
            closeDropdowns(header);
        }
    });
}

// 3) Account dropdown — same show/hide pattern, one panel, right-aligned by CSS.
function initAccountMenu(header) {
    const toggle = header.querySelector('.zit-cs-account-toggle');
    const menu = header.querySelector('.zit-cs-account-menu');

    if (!toggle || !menu) {
        return;
    }

    toggle.addEventListener('click', function () {
        const open = menu.classList.toggle('zit-cs-account-menu-shown');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
}

// 4) Dismissal — a click anywhere outside a menu closes it, and Escape closes
// everything. One document-level pair of listeners covers all three panels.
function initDismiss(header) {
    document.addEventListener('click', function (event) {
        if (!event.target.closest('.zit-cs-topnav-item')) {
            closeDropdowns(header);
        }

        // The account toggle is excluded so its own click still toggles (this listener
        // would otherwise close the menu the toggle just opened).
        if (!event.target.closest('.zit-cs-account')) {
            closeAccountMenu(header);
        }
    });

    document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') {
            return;
        }

        closeDropdowns(header);
        closeAccountMenu(header);
    });
}

function closeAccountMenu(header) {
    const toggle = header.querySelector('.zit-cs-account-toggle');
    const menu = header.querySelector('.zit-cs-account-menu');

    if (menu) {
        menu.classList.remove('zit-cs-account-menu-shown');
    }

    if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
    }
}
